import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { parseExpression } from "cron-parser";
import { EXTENDED_VIEW_WIDTH, MOBILE_WIDTH } from "constants/common";
import {
  FtTable,
  SortType,
  FtTableHeadData,
} from "components/common/table/FtTable";
import { TableActionIcons } from "components/common/table/tableActionIcons/TableActionIcons";

import { useScreenWidth } from "hooks/useScreenWidth";
import { useRequestDispatch } from "store/requestHooks";
import { useRootSelector } from "store/hooks";

import {
  activeAccountTypeConfig,
  currentAutoPay,
  selectedAccountInfo,
} from "store/account/account-selector";
import { selectAllschedulePayments } from "store/schedulePayments/schedulePayments-slice";
import {
  SchedulePayments,
  ScheduledPaymentsFilter,
} from "store/schedulePayments/schedulePayments-state";
import {
  FetchSchedulePayments,
  fetchSchedulePayments,
} from "store/schedulePayments/schedulePayments-action";

import { calculateFee, determineFeeConfiguration, typeMapper } from "utility";
import classes from "./scheduledPayments.module.scss";

import { selectAllPaymentMethods } from "store/paymentMethods/paymentMethods-slice";

export const ScheduledPayments = () => {
  const requestDispatch = useRequestDispatch();
  const { t } = useTranslation(["common"]);
  const accountInfo = useRootSelector(selectedAccountInfo);
  const autoPayInfo = useRootSelector(currentAutoPay);
  const width = useScreenWidth();

  const expandedView = width <= EXTENDED_VIEW_WIDTH;
  const mobileView = width <= MOBILE_WIDTH;

  useEffect(() => {
    requestDispatch<FetchSchedulePayments, ScheduledPaymentsFilter>(
      fetchSchedulePayments,
      createFilterProps(),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountInfo]);

  const schedulePayments = useRootSelector(selectAllschedulePayments);
  const { meta, serviceMeta } = useRootSelector((state) => state.schedule);

  const autoPayDateWithLabel = (amount: number, date: string) => {
    return `${t("common:autopay_settings", {
      amount,
      date,
    })}`;
  };

  const convertCronToDate = (cronExp: string) => {
    const interval = parseExpression(cronExp);
    return interval.next().toDate().toDateString();
  };

  const paymentMethods = useRootSelector(selectAllPaymentMethods);
  const { paymentCenter } = useRootSelector((state) => state.config);

  const config = useRootSelector(activeAccountTypeConfig);
  const getPaymentMethod = (id: number) => {
    return paymentMethods.find((method) => method.id === id);
  };
  const getPaymentMethodType = (id: number) => {
    const method = getPaymentMethod(id);
    return method ? method.paymentMethodType : null;
  };

  const headData: FtTableHeadData[] = [
    {
      id: "status",
      width: expandedView ? 49 : 79,
      label: "",
      alwaysShow: true,
      sortable: true,
      dynamicRender: ({ value }) => (
        <ft-icon
          aria-label={typeMapper(value!, "ScheduleStatusReverse")}
          class={classes[`scheduledStatus${value}`]}
        >
          {value === 0
            ? "event_available"
            : value === 1
              ? "event_busy"
              : "event"}
        </ft-icon>
      ),
    },
    {
      id: "scheduledPaymentDate",
      label: t(`payment:paymentDate`),
      alwaysShow: true,
      sortable: true,
      dynamicRender: ({ t, value }) => {
        const index = value.indexOf("Z");
        if (index >= 0) {
          value = value.substring(0, index) as unknown as Date;
        }
        return t(`common:date_format`, {
          value,
        });
      },
    },
    {
      id: "paymentMethodName",
      label: "",
      dynamicRender({ value, data, t }) {
        if (value) {
          if (value === "ACH") {
            return t(`common:${value?.toLowerCase()}_label`);
          }
          return value;
        }
        if (data?.paymentMethodId != null) {
          const method = getPaymentMethod(data.paymentMethodId);
          if (method?.paymentMethodName) {
            return method.paymentMethodName;
          }

          const type = getPaymentMethodType(data.paymentMethodId);
          if (type != null) {
            return t(
              `common:${typeMapper(type, "paymentMethodTypeReverse")}_label`,
            );
          }
        }
        return "-";
      },
    },
    {
      id: "paymentAmount",
      label: t(`payment:amountPaid`),
      alwaysShow: true,
      sortable: true,
      dynamicRender: ({ t, value, data }) => {
        const paymentMethodType = getPaymentMethodType(data.paymentMethodId);
        const paymentMethod = getPaymentMethod(data.paymentMethodId);

        let feeTotal = 0;
        if (paymentMethodType) {
          const paymentMethodKey = typeMapper(
            paymentMethodType,
            "paymentMethodTypeReverse",
          );
          const paymentMethodFee = determineFeeConfiguration(
            paymentMethod,
            paymentMethodKey ? config.paymentMethods[paymentMethodKey] : null,
            data.recurrenceScheduleId ? true : false,
            paymentCenter.defaultFees.convenienceFee!,
          );
          feeTotal = calculateFee(
            paymentMethodFee,
            paymentCenter.defaultFees.convenienceFee,
            value,
          );
          value = value + feeTotal;
        }
        return value
          ? t(`common:currency_format`, {
              value,
            })
          : "-";
      },
    },
    {
      id: "recurrenceScheduleId",
      label: t(`payment:schedule_source`),
      alwaysShow: false,
      dynamicRender: ({ value }) => {
        return value ? t(`common:autopay_label`) : t(`payment:onetime`);
      },
    },
    {
      id: "created",
      label: t(`common:created`),
      sortable: true,
      alwaysShow: false,
      dynamicRender: ({ t, value }) => {
        const index = value.indexOf("Z");
        if (index >= 0) {
          value = value.substring(0, index) as unknown as Date;
        }
        return t(`common:date_format`, {
          value,
        });
      },
    },
    {
      id: "action",
      label: "",
      type: "ACTION",
      align: "center",
      dynamicRender(props) {
        if (props.data.status === 0) {
          return <TableActionIcons {...props} />;
        }
      },
    },
  ];

  const getLabelTranslation = (id: string) => {
    return t(`payment:${id}`);
  };

  const getTableItem = (data: SchedulePayments[]) => {
    const transformedItem = data.map((single: SchedulePayments) => {
      return {
        ...single,
      };
    });
    return transformedItem;
  };

  const getHeadObject = (head: FtTableHeadData[]) => {
    const headItems = head;
    const transformedHeadItem = headItems.map((single: FtTableHeadData) => {
      return {
        ...single,
        label: single.label ? single.label : getLabelTranslation(single.id),
      };
    });
    return transformedHeadItem;
  };

  const createFilterProps = () => {
    return {
      accountInformationId: accountInfo?.id!,
      status: [0, 1],
    };
  };

  let totalRowCount = serviceMeta?.totalCount;
  if (
    totalRowCount &&
    schedulePayments &&
    schedulePayments.length > totalRowCount
  ) {
    totalRowCount = 0;
  }

  return (
    <section className={classes.Wrapper}>
      {accountInfo ? (
        <>
          <FtTable
            tableHeight={""}
            dataAction={fetchSchedulePayments}
            headData={getHeadObject(headData)}
            tableData={getTableItem(schedulePayments ? schedulePayments : [])}
            autoHeight={true}
            fillHeight={false}
            pagination={false}
            filters={null}
            loading={meta?.isLoading}
            createFilterProps={createFilterProps}
            totalCount={totalRowCount}
            pageCount={4}
            pageNum={serviceMeta?.pageNum}
            expandedView={expandedView}
            showHeader={!expandedView}
            shortPagination={mobileView ? true : false}
            sortColumn={
              typeMapper(
                serviceMeta?.sortColumn ? serviceMeta.sortColumn : "",
                "ScheduleSortColumnReverse",
              ) as string
            }
            sortDir={
              typeMapper(
                serviceMeta?.sortDir ? serviceMeta.sortDir : "",
                "SortDirReverse",
              ) as SortType
            }
            sortMap="ScheduleSortColumn"
            emptyMessage={t("common:no_data_found_scheduled_payment")}
          />
          <div className={classes.DetailContainer}>
            {autoPayInfo?.status === 1 ? (
              <>
                <ft-divider class={classes.Divider}></ft-divider>
                <div className={classes.DetailInfo}>
                  <div className={classes.ExtraDetail}>
                    <div className={classes.AutoPayWrapper}>
                      {t("common:autopay_label")}:{" "}
                      <span
                        className={
                          classes[`autoPayStatus${autoPayInfo?.status}`]
                        }
                      >
                        {autoPayInfo?.status === 1
                          ? t("common:on")
                          : t("common:off")}
                      </span>
                    </div>
                  </div>
                </div>
                <div className={classes.DetailPayment}>
                  <div className={classes.Details}>
                    <div className={classes.AutoPayDate}>
                      {autoPayInfo?.status === 1
                        ? autoPayDateWithLabel(
                            autoPayInfo.paymentAmount != null
                              ? autoPayInfo.paymentAmount
                              : accountInfo.amountDue,
                            autoPayInfo.scheduleExpression
                              ? convertCronToDate(
                                  autoPayInfo.scheduleExpression,
                                )
                              : accountInfo?.dueDate,
                          )
                        : ""}
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <></>
            )}
          </div>
        </>
      ) : (
        <div className={classes.LoaderWrapper}>
          <ft-loader
            styleOverrides={JSON.stringify({ height: "20px", width: "40%" })}
            count={1}
          ></ft-loader>
          <ft-loader
            styleOverrides={JSON.stringify({ height: "35px" })}
            count={3}
          ></ft-loader>
        </div>
      )}
    </section>
  );
};
